/**
 * 检测当前是否运行在 PWA standalone 模式。
 *
 * 三种来源:
 * 1. CSS media query `display-mode: standalone` —— Android Chrome / 桌面 PWA / iOS 16.4+;
 * 2. `navigator.standalone === true` —— iOS Safari 旧版 (12+);
 * 3. URL query `?standalone=1` —— 给调试用 (本地预览强制 standalone 视觉);
 *
 * 同时暴露平台判断, 用于决定要不要弹"添加到主屏幕"引导。
 */

import { computed, onMounted, onUnmounted, ref } from 'vue'

type IOSNavigator = Navigator & { standalone?: boolean }

const STANDALONE_MQ = '(display-mode: standalone)'

function readStandalone(): boolean {
  if (typeof window === 'undefined') return false
  if (window.matchMedia?.(STANDALONE_MQ).matches) return true
  if ((navigator as IOSNavigator).standalone === true) return true
  if (new URLSearchParams(location.search).has('standalone')) return true
  return false
}

function detectIOS(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  // 经典 iOS UA + iPadOS 13+ (Mac-like UA 但 maxTouchPoints>1)
  const macTouch = navigator.platform === 'MacIntel'
    && typeof navigator.maxTouchPoints === 'number'
    && navigator.maxTouchPoints > 1
  return /iPad|iPhone|iPod/.test(ua) || macTouch
}

function detectIOSSafari(): boolean {
  if (!detectIOS()) return false
  const ua = navigator.userAgent || ''
  // 排除 Chrome iOS (CriOS) / Firefox iOS (FxiOS) / Edge iOS (EdgiOS):
  // 这些壳子里没有"添加到主屏幕"那条路径 (实际上能加, 但走的也是 Safari 的同一机制),
  // 不过它们的 hint 文案应该一样, 所以这里宽松判定。
  return !/CriOS|FxiOS|EdgiOS/.test(ua) && /Safari/.test(ua)
}

export function useStandalone() {
  const isStandalone = ref(readStandalone())

  let mq: MediaQueryList | null = null
  const handler = () => { isStandalone.value = readStandalone() }

  onMounted(() => {
    mq = window.matchMedia(STANDALONE_MQ)
    if (mq.addEventListener) mq.addEventListener('change', handler)
    else                     mq.addListener(handler)
  })

  onUnmounted(() => {
    if (mq) {
      if (mq.removeEventListener) mq.removeEventListener('change', handler)
      else                        mq.removeListener(handler)
    }
  })

  return {
    isStandalone,
    isIOS:       computed(() => detectIOS()),
    isIOSSafari: computed(() => detectIOSSafari()),
  }
}
